import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { ChangeDetectorRef, Component, DestroyRef, inject, Inject, OnInit, signal } from '@angular/core';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatLabel } from '@angular/material/form-field';
import { firstValueFrom, from, Observable, of, startWith, switchMap } from 'rxjs';
import { Store } from '@ngxs/store';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MatOption, MatSelect } from '@angular/material/select';
import { normalizeManyRelations, normalizeOneRelation } from '../../core/decorators/relation.decorator';
import { map } from 'rxjs/operators';
import {
  MultiComboEntitySearchComponent
} from '../../utils/component/multi-combo-entity-search/multi-combo-entity-search.component';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDivider } from '@angular/material/divider';
import { LearningUnitEntity } from '../../learning-unit/learning-unit-entity';
import { StateFilterCBComponent } from '../../utils/component/state-filter-combobox/state-cb.component';
import { EntityStateModel } from '../../core/state/entity.state';
import { MatIcon } from '@angular/material/icon';
import { MediaType } from '../../core/types';
import { ChooseMedium } from '../../utils/component/choose-medium/choose-medium.json.model';
import { ChooseMediumComponent } from '../../utils/component/choose-medium/choose-medium.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AssetJsonModel } from '../../asset/assets.json.model';
import { environment } from '../../../environments/environment';
import { ConfirmDialogComponent } from '../../core/components/confirm-delete-dialog/confirm-dialog.component';
import { LearningSectionEntity } from '../../learning-section/learning-section-entity';
import { ModuleEntityService } from '../../module/services/module.entity.service';
import { LearningSectionEntityService } from '../../learning-section/services/learning-section.entity.service';
import { ModuleEntity } from '../../module/module.entity';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { ConsumerEntityService, ConsumerEntityState } from '../../consumer/services/consumer.entity.service';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import {
  ObjectConsumerMetadataComponent
} from '../../consumer/object-consumer-metadata/object-consumer-metadata.component';
import { RelationRef } from '../../core/types/entity-types';
import {
  GlobalMetaDataEntityService
} from '../../global-metadata/services/global-metadata.entity.service';
import { GlobalMetaDataEntity, MetadataField } from '../../global-metadata/global-metadata.entity';
import {
  ModuleLearningUnitMetadataEntityService
} from '../../module/services/module-learning-unit-metadata.entity.service';
import { ModuleLearningUnitMetadataEntity } from '../../module/module-learning-unit-metadata.entity';

export interface LuMetadataRow {
  id?: string
  cantonKey: string
  professionKey: string
  moduleIds: string[]
}

export interface LuMetadataSelectOption {
  id: string
  label: string
}

@Component({
  selector: 'pixl-form-dialog-learning-unit',
  templateUrl: './form-dialog-learning-unit.component.html',
  imports: [
    CommonModule,
    MatDialogContent,
    MatFormField,
    MatDialogTitle,
    MatLabel,
    MatFormField,
    MatInput,
    MatFormField,
    MatDialogActions,
    ReactiveFormsModule,
    MatButton,
    MatSelect,
    MatOption,
    FormsModule,
    MultiComboEntitySearchComponent,
    CdkDrag,
    CdkDropList,
    MatDivider,
    StateFilterCBComponent,
    MatIcon,
    MatIconButton,
    AsyncPipe,
    MatTabGroup,
    MatTab,
    ObjectConsumerMetadataComponent
  ],
})
export class FormDialogLearningUnitComponent implements OnInit {
  form: FormGroup;
  consumers$!: Observable<ConsumerEntity[]>;
  learningUnits$!: Observable<LearningUnitEntity[]>;
  modules$!: Observable<ModuleEntity[]>;
  learningSections$!: Observable<LearningSectionEntity[]>;
  selectedConsumerName$!: Observable<string>;

  private store = inject(Store);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private consumerService = inject(ConsumerEntityService);
  private moduleService = inject(ModuleEntityService);
  private learningSectionService = inject(LearningSectionEntityService);
  private globalMetadataService = inject(GlobalMetaDataEntityService);
  private metadataEntityService = inject(ModuleLearningUnitMetadataEntityService);

  selectedModules: ModuleEntity[] = [];
  currentPageModules = 1;
  pageSizeModules = 20;
  searchQueryModules: string | null = null;

  selectedLearningSections: LearningSectionEntity[] = [];
  currentPageLearningSections = 1;
  pageSizeLearningSections = 20;
  searchQueryLearningSections: string | null = null;

  public imageUrl = signal('');

  status = '';

  // ABU metadata
  isAbuConsumer = false;
  cantonOptions: MetadataField[] = [];
  professionOptions: MetadataField[] = [];
  cantonSelectOptions: LuMetadataSelectOption[] = [];
  professionSelectOptions: LuMetadataSelectOption[] = [];
  metadataRows: LuMetadataRow[] = [];
  metadataRowCantonOptions$: Observable<LuMetadataSelectOption[]>[] = [];
  metadataRowProfessionOptions$: Observable<LuMetadataSelectOption[]>[] = [];
  metadataRowSelectedCantons: LuMetadataSelectOption[][] = [];
  metadataRowSelectedProfessions: LuMetadataSelectOption[][] = [];
  metadataRowModules$: Observable<ModuleEntity[]>[] = [];
  metadataRowSelectedModules: ModuleEntity[][] = [];
  metadataRowModuleSearchQueries: (string | null)[] = [];
  metadataRowModuleCurrentPages: number[] = [];
  metadataRowModulePageSize = 20;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<FormDialogLearningUnitComponent>,
    @Inject(MAT_DIALOG_DATA) public data?: Partial<LearningUnitEntity>,
  ) {
    this.form = this.fb.group({
      title: [data?.title || ''],
      subtitle: [data?.subtitle || ''],
      description: [data?.description || ''],
      status: [data?.status || 'Edit'],
      preview: [data?.preview || []],
      lesson_section: [data?.lesson_section || ''],
      processing_time: [data?.processing_time || ''],
      // federal_state: [data?.federal_state || []],
      learning_unit_goal: [data?.learning_unit_goal || ''],
      learning_unit_type: [data?.learning_unit_type || ''],
      changed_by: [data?.changed_by || ''],
      consumer: [normalizeOneRelation<ConsumerEntity>(data?.consumer as RelationRef<ConsumerEntity>[])],
      consumerData: this.fb.group(
        Object.fromEntries(
          Object.entries(data?.consumerData || {}).map(
            ([key, value]) => [key, Array.isArray(value) ? [value] : value]
          )
        )
      ),
      modules: [normalizeManyRelations(data?.modules)],
      learningSections: [normalizeManyRelations(data?.learningSections)],
    });
    this.buildURLs();
  }

  ngOnInit() {
    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery))
      )
    );

    this.selectedConsumerName$ = this.form.get('consumer')!.valueChanges.pipe(
      startWith(this.form.get('consumer')!.value),
      switchMap(val => {
        const id = Array.isArray(val) ? val[0] : val;
        if (!id) {
          return of('Metadaten');
        }
        return this.store.select(ConsumerEntityState.byId).pipe(
          map(fn => fn(id)),
          map(consumer => consumer?.name || 'Metadaten')
        );
      })
    );

    const moduleQuery = { 'page[size]': this.pageSizeModules, 'page[number]': this.currentPageModules, 'sort': 'title' };

    this.modules$ = from(
      this.moduleService.ensureLoaded(moduleQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.moduleService.allByQuery(moduleQuery)).pipe(
          map(paginated => {
            const combined = [...this.selectedModules, ...paginated].filter(
              (lu, index, self) => index === self.findIndex(s => s.id === lu.id)
            );
            return combined;
          })
        )
      )
    );

    const selectedModuleIds = normalizeManyRelations(this.data?.modules) as string[];

    const moduleState = this.store.selectSnapshot(this.moduleService.stateClass) as EntityStateModel<ModuleEntity>;
    this.selectedModules = selectedModuleIds
      .map(id => moduleState.entities[id])
      .filter((e): e is ModuleEntity => !!e);

    this.modules$ = this.store.select(this.moduleService.allByQuery(moduleQuery)).pipe(
      map(paginated => {
        const combined = [...this.selectedModules, ...paginated].filter(
          (seq, index, self) => index === self.findIndex(s => s.id === seq.id)
        );
        return combined;
      })
    );

    this.form.get('modules')?.setValue(this.selectedModules.map(s => s.id));

    const learningSectionQuery = { 'page[size]': this.pageSizeLearningSections, 'page[number]': this.currentPageLearningSections, 'sort': 'name' };

    this.learningSections$ = from(
      this.learningSectionService.ensureLoaded(learningSectionQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.learningSectionService.allByQuery(learningSectionQuery)).pipe(
          map(paginated => {
            const combined = [...this.selectedLearningSections, ...paginated].filter(
              (lu, index, self) => index === self.findIndex(s => s.id === lu.id)
            );
            return combined;
          })
        )
      )
    );

    const selectedLearningSectionIds = normalizeManyRelations(this.data?.learningSections) as string[];

    const learningSectionState = this.store.selectSnapshot(this.learningSectionService.stateClass) as EntityStateModel<LearningSectionEntity>;
    this.selectedLearningSections = selectedLearningSectionIds
      .map(id => learningSectionState.entities[id])
      .filter((e): e is LearningSectionEntity => !!e);

    this.learningSections$ = this.store.select(this.learningSectionService.allByQuery(learningSectionQuery)).pipe(
      map(paginated => {
        const combined = [...this.selectedLearningSections, ...paginated].filter(
          (seq, index, self) => index === self.findIndex(s => s.id === seq.id)
        );
        return combined;
      })
    );

    this.form.get('learningSections')?.setValue(this.selectedLearningSections.map(s => s.id));

    // Watch consumer changes to detect ABU *** (disabled for now)
    // this.form.get('consumer')!.valueChanges.pipe(
    //   startWith(this.form.get('consumer')!.value),
    //   takeUntilDestroyed(this.destroyRef)
    // ).subscribe(val => {
    //   const id = Array.isArray(val) ? val[0] : val;
    //   if (!id) {
    //     this.isAbuConsumer = false;
    //     return;
    //   }
    //   const byIdFn = this.store.selectSnapshot(ConsumerEntityState.byId);
    //   const consumer = byIdFn(id);
    //   this.isAbuConsumer = consumer?.name === 'ABU';
    //   if (this.isAbuConsumer) {
    //     this.loadAbuMetadata();
    //   }
    // });
  }

  private async loadAbuMetadata(): Promise<void> {
    const professionsQuery = { 'filter[name]': 'professions_abu' };
    const cantonsQuery = { 'filter[name]': 'kantone' };

    await this.globalMetadataService.ensureLoaded(professionsQuery);
    await this.globalMetadataService.ensureLoaded(cantonsQuery);

    const professionsMeta = this.store.selectSnapshot(this.globalMetadataService.allByQuery(professionsQuery)) as GlobalMetaDataEntity[];
    const cantonsMeta = this.store.selectSnapshot(this.globalMetadataService.allByQuery(cantonsQuery)) as GlobalMetaDataEntity[];

    this.professionOptions = [];
    if (professionsMeta.length > 0 && professionsMeta[0].fieldConfig) {
      for (const category of professionsMeta[0].fieldConfig) {
        if (category.subFieldConf) {
          for (const profession of category.subFieldConf) {
            this.professionOptions.push(profession);
          }
        }
      }
    }

    this.cantonOptions = [];
    if (cantonsMeta.length > 0 && cantonsMeta[0].fieldConfig) {
      for (const field of cantonsMeta[0].fieldConfig) {
        if (field.subFieldConf && field.subFieldConf.length > 0) {
          this.cantonOptions = field.subFieldConf;
        }
      }
    }

    this.cantonSelectOptions = this.cantonOptions.map(c => ({ id: c.name, label: c.label || c.name }));
    this.professionSelectOptions = this.professionOptions.map(p => ({ id: p.name, label: p.label || p.name }));

    if (this.data?.id) {
      const metadataQuery = { 'filter[learningUnit]': this.data.id, 'include': 'module' };
      await this.metadataEntityService.ensureLoaded(metadataQuery);
      const existingMetadata = this.store.selectSnapshot(
        this.metadataEntityService.allByQuery(metadataQuery)
      ) as ModuleLearningUnitMetadataEntity[];

      const rowMap = new Map<string, LuMetadataRow>();
      for (const entry of existingMetadata) {
        const key = `${entry.cantonKey}::${entry.professionKey}`;
        if (!rowMap.has(key)) {
          rowMap.set(key, {
            cantonKey: entry.cantonKey,
            professionKey: entry.professionKey,
            moduleIds: [],
          });
        }
        const moduleId = this.extractRelationId(entry.module);
        if (moduleId) {
          rowMap.get(key)!.moduleIds.push(moduleId);
        }
      }
      this.metadataRows = Array.from(rowMap.values());

      for (let i = 0; i < this.metadataRows.length; i++) {
        this.initMetadataRowModuleState(i);
        this.initMetadataRowCantonProfessionState(i);
      }
    }

    this.cdr.detectChanges();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private extractRelationId(relation: any): string | null {
    if (!relation) return null;
    if (typeof relation === 'string') return relation;
    if (typeof relation === 'object' && 'id' in relation) return relation.id;
    return null;
  }

  addMetadataRow(): void {
    this.metadataRows = [...this.metadataRows, {
      cantonKey: '',
      professionKey: '',
      moduleIds: [],
    }];
    const index = this.metadataRows.length - 1;
    this.initMetadataRowModuleState(index);
    this.initMetadataRowCantonProfessionState(index);
  }

  removeMetadataRow(index: number): void {
    this.metadataRows = this.metadataRows.filter((_, i) => i !== index);
    this.metadataRowModules$.splice(index, 1);
    this.metadataRowSelectedModules.splice(index, 1);
    this.metadataRowModuleSearchQueries.splice(index, 1);
    this.metadataRowModuleCurrentPages.splice(index, 1);
    this.metadataRowSelectedCantons.splice(index, 1);
    this.metadataRowSelectedProfessions.splice(index, 1);
    this.metadataRowCantonOptions$.splice(index, 1);
    this.metadataRowProfessionOptions$.splice(index, 1);
  }

  receiveMetadataRowCantonData(index: number, selected: LuMetadataSelectOption[] | null): void {
    this.metadataRowSelectedCantons[index] = selected ?? [];
    this.metadataRows[index] = {
      ...this.metadataRows[index],
      cantonKey: selected?.[0]?.id ?? '',
    };
  }

  receiveMetadataRowProfessionData(index: number, selected: LuMetadataSelectOption[] | null): void {
    this.metadataRowSelectedProfessions[index] = selected ?? [];
    this.metadataRows[index] = {
      ...this.metadataRows[index],
      professionKey: selected?.[0]?.id ?? '',
    };
  }

  receiveMetadataRowCantonQuery(index: number, query: string): void {
    const search = query?.trim().toLowerCase() || '';
    const filtered = search ?
      this.cantonSelectOptions.filter(c => c.label.toLowerCase().includes(search)) :
      this.cantonSelectOptions;
    this.metadataRowCantonOptions$[index] = of(filtered);
  }

  receiveMetadataRowProfessionQuery(index: number, query: string): void {
    const search = query?.trim().toLowerCase() || '';
    const filtered = search ?
      this.professionSelectOptions.filter(p => p.label.toLowerCase().includes(search)) :
      this.professionSelectOptions;
    this.metadataRowProfessionOptions$[index] = of(filtered);
  }

  getMetadataRowCantonLabel(index: number): string {
    const selected = this.metadataRowSelectedCantons[index];
    if (!selected || !selected.length) {
      return 'Kanton';
    }
    return selected.map(c => c.label).join(', ');
  }

  getMetadataRowProfessionLabel(index: number): string {
    const selected = this.metadataRowSelectedProfessions[index];
    if (!selected || !selected.length) {
      return 'Beruf';
    }
    return selected.map(p => p.label).join(', ');
  }

  receiveMetadataRowModuleData(index: number, selected: ModuleEntity[] | null): void {
    this.metadataRowSelectedModules[index] = selected ?? [];
    this.metadataRows[index] = {
      ...this.metadataRows[index],
      moduleIds: (selected ?? []).map(m => m.id),
    };
  }

  async receiveMetadataRowModuleQuery(index: number, query: string): Promise<void> {
    this.metadataRowModuleSearchQueries[index] = query?.trim() || null;

    if (this.metadataRowModuleSearchQueries[index] && this.metadataRowModuleSearchQueries[index]!.length > 3) {
      const searchParams = { 'filter[titleLike]': this.metadataRowModuleSearchQueries[index]! };
      await this.moduleService.ensureLoaded(searchParams);
      this.metadataRowModules$[index] = this.store.select(this.moduleService.allByQuery(searchParams));
    }
    else {
      const pageQuery = { 'page[size]': this.metadataRowModulePageSize, 'page[number]': 1, 'sort': 'title' };
      this.metadataRowModules$[index] = this.store.select(this.moduleService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          return [...this.metadataRowSelectedModules[index], ...paginated]
            .filter((m, i, self) => i === self.findIndex(s => s.id === m.id));
        })
      );
    }
  }

  async loadMoreMetadataRowModules(index: number, event: { pageNumber: number, pageSize: number }): Promise<void> {
    if (!this.metadataRowModuleSearchQueries[index]) {
      const pageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'title',
      };
      await this.moduleService.ensureLoaded(pageQuery);

      const currentModules = await firstValueFrom(this.metadataRowModules$[index] ?? of([]));
      this.metadataRowModules$[index] = this.store.select(this.moduleService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          return [
            ...this.metadataRowSelectedModules[index],
            ...currentModules,
            ...paginated,
          ].filter((m, i, self) => i === self.findIndex(s => s.id === m.id));
        })
      );
    }
  }

  getMetadataRowModuleLabel(index: number): string {
    const selected = this.metadataRowSelectedModules[index];
    if (!selected || !selected.length) {
      return 'Module';
    }
    return selected.map(m => m.title).join(', ');
  }

  reOrderMetadataRowModules(index: number, event: CdkDragDrop<ModuleEntity[]>): void {
    moveItemInArray(this.metadataRowSelectedModules[index], event.previousIndex, event.currentIndex);
    this.metadataRows[index] = {
      ...this.metadataRows[index],
      moduleIds: this.metadataRowSelectedModules[index].map(m => m.id),
    };
  }

  private initMetadataRowCantonProfessionState(index: number): void {
    const row = this.metadataRows[index];
    this.metadataRowSelectedCantons[index] = row.cantonKey ?
      this.cantonSelectOptions.filter(c => c.id === row.cantonKey) :
      [];
    this.metadataRowSelectedProfessions[index] = row.professionKey ?
      this.professionSelectOptions.filter(p => p.id === row.professionKey) :
      [];
    this.metadataRowCantonOptions$[index] = of(this.cantonSelectOptions);
    this.metadataRowProfessionOptions$[index] = of(this.professionSelectOptions);
  }

  private initMetadataRowModuleState(index: number): void {
    const row = this.metadataRows[index];
    const state = this.store.selectSnapshot(this.moduleService.stateClass) as EntityStateModel<ModuleEntity>;
    this.metadataRowSelectedModules[index] = row.moduleIds
      .map(id => state.entities[id])
      .filter((e): e is ModuleEntity => !!e);
    this.metadataRowModuleSearchQueries[index] = null;
    this.metadataRowModuleCurrentPages[index] = 1;

    const pageQuery = { 'page[size]': this.metadataRowModulePageSize, 'page[number]': 1, 'sort': 'title' };
    this.metadataRowModules$[index] = this.store.select(this.moduleService.allByQuery(pageQuery)).pipe(
      map(paginated => {
        return [...this.metadataRowSelectedModules[index], ...paginated]
          .filter((m, i, self) => i === self.findIndex(s => s.id === m.id));
      })
    );
  }

  receiveModuleData(selected: ModuleEntity[] | null) {
    this.selectedModules = selected ?? [];
    this.form.get('modules')?.setValue(this.selectedModules.map(s => s.id));
  }

  async receiveModuleQuery(query: string) {
    this.searchQueryModules = query?.trim() || null;

    if (this.searchQueryModules && this.searchQueryModules.length > 3) {
      // search mode
      const searchParams = { 'filter[titleLike]': this.searchQueryModules };
      await this.moduleService.ensureLoaded(searchParams);

      this.modules$ = this.store.select(this.moduleService.allByQuery(searchParams)).pipe(
        map(results => results)
      );
    }
    else {
      // default paginated mode
      const pageQuery = { 'page[size]': this.pageSizeModules, 'page[number]': this.currentPageModules, 'sort': 'title' };
      this.modules$ = this.store.select(this.moduleService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          const combined = [...this.selectedModules, ...paginated]
            .filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
          return combined;
        })
      );
    }
  }

  async loadMoreModules(event: { pageNumber: number, pageSize: number }) {
    if (!this.searchQueryModules) {
      const pageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'title',
      };

      await this.moduleService.ensureLoaded(pageQuery);

      const currentModules = await firstValueFrom(this.modules$ ?? of([]));

      this.modules$ = this.store.select(this.moduleService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          const combined = [
            ...this.selectedModules,
            ...currentModules,
            ...paginated,
          ].filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
          return combined;
        })
      );
    }
  }

  get moduleLabel(): string {
    if (!this.selectedModules || !this.selectedModules.length) {
      return 'Module';
    }
    return this.selectedModules.map(module => module.title).join(', ');
  }

  receiveLearningSectionData(selected: LearningSectionEntity[] | null) {
    this.selectedLearningSections = selected ?? [];
    this.form.get('learningSections')?.setValue(this.selectedLearningSections.map(s => s.id));
  }

  async receiveLearningSectionQuery(query: string) {
    this.searchQueryLearningSections = query?.trim() || null;

    if (this.searchQueryLearningSections && this.searchQueryLearningSections.length > 3) {
      // search mode
      const searchParams = { 'filter[nameLike]': this.searchQueryLearningSections };
      await this.learningSectionService.ensureLoaded(searchParams);

      this.learningSections$ = this.store.select(this.learningSectionService.allByQuery(searchParams)).pipe(
        map(results => results)
      );
    }
    else {
      // default paginated mode
      const pageQuery = { 'page[size]': this.pageSizeLearningSections, 'page[number]': this.currentPageLearningSections, 'sort': 'name' };
      this.learningSections$ = this.store.select(this.learningSectionService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          const combined = [...this.selectedModules, ...paginated]
            .filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
          return combined;
        })
      );
    }
  }

  async loadMoreLearningSections(event: { pageNumber: number, pageSize: number }) {
    if (!this.searchQueryLearningSections) {
      const pageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'name',
      };

      await this.learningSectionService.ensureLoaded(pageQuery);

      const currentLearningSections = await firstValueFrom(this.learningSections$ ?? of([]));

      this.learningSections$ = this.store.select(this.learningSectionService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          const combined = [
            ...this.selectedLearningSections,
            ...currentLearningSections,
            ...paginated,
          ].filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
          return combined;
        })
      );
    }
  }

  reOrderSelectedLearningSections(event: CdkDragDrop<LearningSectionEntity[]>) {
    moveItemInArray(this.selectedLearningSections, event.previousIndex, event.currentIndex);
    this.form.get('learningSections')?.setValue(this.selectedLearningSections.map(lu => lu.id));
  }

  public get learningSectionLabel(): string {
    if (!this.selectedLearningSections || !this.selectedLearningSections.length) {
      return 'Lernabschnitte';
    }
    return this.selectedLearningSections.map(learningSection => learningSection.name).join(', ');
  }

  public openImageChoice(): void {
    this.showMediumDialog('Image');
  }

  public showMediumDialog(mediaType: MediaType): void {
    const chooseMedium: ChooseMedium = {
      mediaType: mediaType,
      desktopAudio: false,
      subjectId: ''
    };

    const dialogRef = this.dialog.open(ChooseMediumComponent, {
      minWidth: '60vw',
      maxWidth: '80vw',
      minHeight: '530px',
      maxHeight: '530px',
      data: chooseMedium
    });

    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((answer: AssetJsonModel) => {
        if (!answer) return;

        const previewControl = this.form.get('preview');

        if (!previewControl?.value || Array.isArray(previewControl.value)) {
          // initialize as object if empty
          previewControl?.setValue({ id: '', name: '', mediaType: '', media_url: '' });
        }

        // update the form control value
        previewControl?.patchValue({
          id: answer.id,
          mediaType: 'Image',
          media_url: answer.media_url,
          name: answer.name
        });

        this.cdr.detectChanges();
        this.buildURLs();
      });
  }

  private buildURLs(): void {
    const baseUrl = environment.isProd ? '/' : 'https://zav-stage.westermann.de/';

    const preview = this.form.get('preview')?.value;

    if (preview && preview.media_url) {
      this.imageUrl.set(baseUrl + preview.media_url);
    }
  }

  public deleteMediaEntry(): void {
    const previewControl = this.form.get('preview');
    const preview = previewControl?.value;

    const id = preview?.id;
    const name = preview?.name;
    const headerText = 'Bild-Datei löschen';

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        id,
        name,
        itemType: 'Datei',
        headerText,
        deleteFn: () => {
          // clear the form control instead of this.data.preview
          previewControl?.setValue({ id: '', name: '', mediaType: '', media_url: '' });
          this.cdr.detectChanges();
          dialogRef.close();
        }
      },
    });
  }

  public getImageName(): string {
    const preview = this.form.get('preview')?.value;
    return preview?.name || '';
  }

  statusChanged(newStatus: string) {
    this.status = newStatus;
  }

  save(): void {
    if (this.form.valid) {
      const formValue = this.form.value;

      const updatedLearningUnit = Object.assign(
        new LearningUnitEntity(),
        { id: this.data?.id },
        formValue,
        {
          consumer: formValue.consumer ? { type: 'consumers', id: formValue.consumer } : null,
          modules: (formValue.modules as string[])?.length ?
            (formValue.modules as string[]).map(id => ({ id })) :
            [],
          learningSections: (formValue.learningSections as string[])?.length ?
            (formValue.learningSections as string[]).map(id => ({ id })) :
            [],
        }
      );

      // Attach metadata rows for the caller to handle
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (updatedLearningUnit as any)._metadataRows = this.isAbuConsumer ? this.metadataRows : undefined;

      this.dialogRef.close(updatedLearningUnit);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  trackLearningSectionsById(index: number, item: LearningSectionEntity) {
    return item.id;
  }

  trackById(index: number, item: ModuleEntity) {
    return item.id;
  }
}
